<script setup>
import { ref, computed, watch } from 'vue'
import { useTranslaterStore } from '@/stores/translater'
import { useCarGroupsStore } from '@/stores/car-groups'
import Modal from '@/components/UTAModal.vue'
import UTACarGroupCreate from '@/components/car/UTACarGroupCreate.vue'

function changePage(cursor, dir) {

    const params = {
        car_array: props.uuid, 
        'c.limit': 12, 
        'c.direction': dir, 
        'c.cursor': cursor
    }

    carGroupsStore.readCarGroups(params).then(result => {
        if(result.cars) {
            dir == 'after' ? 
                pageNumber.value++ : 
                pageNumber.value--
        }
    })
}

const props = defineProps({
    uuid: {
        type: String,
        required: true
    }
})

const translaterStore   = useTranslaterStore()
const carGroupsStore    = useCarGroupsStore()

const getTranslate              = computed(() => translaterStore.getTranslate)
const carGroups                 = computed(() => carGroupsStore.carGroups)
const carGroupsProcess          = computed(() => carGroupsStore.carGroupsProcess)
const carGroupsMsg              = computed(() => carGroupsStore.carGroupsMsg)
const carGroupsCreateMsg        = computed(() => carGroupsStore.carGroupsCreateMsg)
const carGroupsDeleteProcess    = computed(() => carGroupsStore.carGroupsDeleteProcess)
const carGroupsDeleteMsg        = computed(() => carGroupsStore.carGroupsDeleteMsg)

const viewMode              = ref(localStorage.getItem('UTA_CARS_GROUPS_VIEW') || 'lines') 
const pageNumber            = ref(1) 
const inputSearch           = ref('') 
const deleteGroupUUID       = ref(null) 
const showCreateCarsGroup   = ref(false) 
const showDeleteCarsGroup   = ref(false)

function setViewMode(mode) {

    viewMode.value = mode
    localStorage.setItem('UTA_CARS_GROUPS_VIEW', mode)
}

carGroupsStore.readCarGroups({ car_array: props.uuid, 'c.limit': 12 })

watch(inputSearch, input => {
    input.length > 2 ? 
        carGroupsStore.readCarGroups({ car_array: props.uuid, 'c.limit': 12, 'c.q': inputSearch.value }) :
        carGroupsStore.readCarGroups({ car_array: props.uuid, 'c.limit': 12 })
})

watch(carGroupsCreateMsg, msg => {
    if('success' in msg && msg.success) {
        carGroupsStore.readCarGroups({ car_array: props.uuid, 'c.limit': 12 })
        showCreateCarsGroup.value = false
    }
})

watch(carGroupsDeleteMsg, msg => {
    if('success' in msg && msg.success) {
        carGroupsStore.readCarGroups({ car_array: props.uuid, 'c.limit': 12 })
        showDeleteCarsGroup.value = false
    }
})
</script>

<template>

    <div class="mb-4 uta-cars-groups">

        <header class="page__header uta-header" role="heading" aria-level="1">
            <h2 class="uta-header__title">{{ getTranslate.CAR_GROUPS }}</h2>
            <div class="spinner-border uta-header__spinner" role="status" v-if="carGroupsProcess && carGroups.array?.length">
                <span class="visually-hidden">Loading...</span>
            </div>
            <div class="uta-header__actions">
                <a 
                    href="#" 
                    class="uta-btn uta-btn--main uta-btn--sm uta-btn--has-icon uta-header__action uta-header__action--create"
                    @click.prevent="showCreateCarsGroup = true"
                >
                    <span class="uta-btn__content">
                        <span class="uta-btn__icon material-icons">settings</span>
                        <span class="uta-btn__text uta-btn__text--has-icon d-none d-md-block">{{ getTranslate.BTN_CTRL }}</span>
                    </span>
                </a>
            </div>
        </header>
    
        <div class="page__content">
            <div class="row mb-4 justify-content-between">
                <div class="col-12 col-md-7 col-lg-10">
                    <div class="uta-input__wrapper uta-search">
                        <input 
                            type="text" 
                            :placeholder="getTranslate.SEARCH + '...'" 
                            v-model="inputSearch"
                            @keyup.escape="inputSearch = ''"
                            class="form-control uta-input__wrapper-input uta-search__input" 
                        >
                        <template v-if="inputSearch.length > 2">
                            <span class="material-icons uta-input__wrapper-icon" @click="inputSearch = ''">
                                cancel
                            </span>
                        </template>
                        <template v-else>
                            <span class="material-icons uta-input__wrapper-icon">
                                search
                            </span>
                        </template>
                    </div>
                </div>
                <div class="d-none d-md-flex view-selector">
                    <a href="#" class="view-selector__item view-selector__item--lines" @click.prevent="setViewMode('lines')"></a>
                    <a href="#" class="view-selector__item view-selector__item--grid" @click.prevent="setViewMode('grid')"></a>
                </div>
            </div>
            <div
                v-if="'success' in carGroupsCreateMsg && carGroupsCreateMsg.success" 
                class="my-4 uta-alert uta-alert--success"
            >
                <span class="material-icons uta-alert__icon">check_circle_outline</span>
                <div class="uta-alert__text">
                    {{ getTranslate[carGroupsCreateMsg.text] || carGroupsCreateMsg.text }}
                </div>
                <button type="button" class="uta-alert__close" @click="carGroupsStore.setCarGroupsCreateMsg({})">
                    <span class="material-icons">close</span>
                </button>
            </div>
            <div 
                v-if="'success' in carGroupsDeleteMsg && carGroupsDeleteMsg.success"
                class="my-4 uta-alert uta-alert--success"
            >
                <span class="material-icons uta-alert__icon">check_circle_outline</span>
                <div class="uta-alert__text">
                    {{ getTranslate[carGroupsDeleteMsg.text] || carGroupsDeleteMsg.text }}
                </div>
                <button type="button" class="uta-alert__close" @click="carGroupsStore.setCarGroupsDeleteMsg({})">
                    <span class="material-icons">close</span>
                </button>
            </div>
            <div  
                v-if="'success' in carGroupsMsg && !carGroupsMsg.success"
                class="my-4 uta-alert uta-alert--danger uta-group-users__message"
            >
                <span class="material-icons uta-alert__icon">error_outline</span>
                <div class="uta-alert__text">
                    {{ getTranslate[carGroupsMsg.text] || carGroupsMsg.text }}
                </div>
            </div>
            <template v-if="carGroups.array?.length">
                <template v-if="viewMode == 'lines'">
                    <div class="container-fluid">
                        <div class="uta-cars-groups__list uta-list">
                            <div class="uta-list__title d-none d-lg-block uta-cars-groups__title">
                                <div class="row">
                                    <div class="col-10">
                                        <div class="row">
                                            <div class="col-5">{{ getTranslate.CARS_GROUPS_NAME }}</div>
                                            <div class="col-7">{{ getTranslate.CARS_GROUPS_DESCR }}</div>
                                        </div>
                                    </div>
                                    <div class="col-2"></div>
                                </div>
                            </div>
                            <div class="pb-4 uta-list__content">
                                <div 
                                    class="row align-items-center uta-list__item uta-cars-groups__group" 
                                    :class="{ 'uta-list__item--dark' : index % 2 }" 
                                    v-for="(group, index) in carGroups.cgroups" 
                                    :key="group.vin"
                                >
                                    <div class="col-10">
                                        <div class="row">
                                            <div class="col-12 col-lg-5 uta-cars-groups__group-name">
                                                <span class="d-inline d-lg-none uta-cars-groups__group-mobile-title">{{ getTranslate.CARS_GROUPS_NAME }}: </span>
                                                <router-link :to="'/cars-groups/' + group.uuid" class="uta-list__item-link uta-cars-groups__link">{{ group.name }}</router-link>
                                            </div>
                                            <div class="col-12 col-lg-7 uta-cars-groups__group-descr">
                                                <span class="d-inline d-lg-none uta-cars-groups__group-mobile-title">{{ getTranslate.CARS_GROUPS_DESCR }}: </span>
                                                {{ group.description }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-2">
                                        <div class="text-end uta-cars-groups__group-delete">
                                            <span 
                                                class="material-icons uta-list__item-icon uta-cars-groups__delete-icon"
                                                @click="
                                                    showDeleteCarsGroup = true, 
                                                    deleteGroupUUID = group.uuid
                                                "
                                            >
                                                delete
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
                <template v-if="viewMode == 'grid'">
                    <div class="row">
                        <div 
                            class="mb-4 col-12 col-xs-6 col-sm-6 col-xl-4 col-xxl-3 uta-cars-groups__group grid" 
                            v-for="group in carGroups.cgroups" 
                            :key="group.uuid"
                        >
                            <div class="grid__item">
                                <span 
                                    class="material-icons grid__item-icon" 
                                    @click="
                                        showDeleteCarsGroup = true, 
                                        deleteGroupUUID = group.uuid
                                    "
                                >
                                    delete
                                </span>
                                <div class="grid__item-title">
                                    {{ group.name }}
                                </div>
                                <div class="grid__item-subtitle">
                                    {{ group.description || '-' }}
                                </div>
                                <router-link :to="'/cars-groups/' + group.uuid" class="grid__item-readmore">
                                    {{ getTranslate.BTN_READMORE }}
                                </router-link>
                            </div>
                        </div>
                    </div>
                </template>
                <div class="uta-cars__pagination" v-if="carGroups.c && (carGroups.c.isBefore || carGroups.c.isAfter)">
                    <div class="row">
                        <div class="col-12 col-xs-6 col-sm-6 col-md-4 col-xl-3 col-xxl-2">
                            <div class="uta-pagination">
                                <button 
                                    :disabled="carGroupsProcess || !carGroups.c.isBefore"
                                    class="uta-btn uta-btn--sm uta-pagination__prev" 
                                    @click.prevent="changePage(carGroups.c.start, 'before')"
                                >
                                    <span class="material-icons">
                                        navigate_before
                                    </span>
                                </button>
                                <div class="uta-pagination__current">
                                    {{ pageNumber }}
                                </div>
                                <button 
                                    :disabled="carGroupsProcess || !carGroups.c.isAfter"
                                    class="uta-btn uta-btn--sm uta-pagination__next" 
                                    @click.prevent="changePage(carGroups.c.stop, 'after')"
                                >
                                    <span class="material-icons">
                                        navigate_next
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
        <transition name="bounce">
            <Modal 
                v-if="showCreateCarsGroup"
                :header="getTranslate.CAR_GROUPS_CREATE"
                size="md"
                @closeModal="showCreateCarsGroup = false, carGroupsStore.setCarGroupsCreateMsg({})"
            >
                <template v-slot:body>
                    <UTACarGroupCreate :uuid="props.uuid" @closeModal="showCreateCarsGroup = false, carGroupsStore.setCarGroupsCreateMsg({})" />
                </template>
            </Modal>
        </transition>
        <transition name="bounce">
            <Modal 
                v-if="showDeleteCarsGroup"
                :header="getTranslate.CAR_GROUPS_DELETE"
                size="md"
                @closeModal="showDeleteCarsGroup = false"
            >
                <template v-slot:body>
                    <form action="" @submit.prevent="carGroupsStore.deleteCarGroups({ cgroup: deleteGroupUUID, car: props.uuid })">
                        {{ getTranslate.CAR_GROUPS_DELETE_DESCR }}
                        <div class="mt-5 uta-btns">
                            <button type="button" class="uta-btn" @click="showDeleteCarsGroup = false">{{ getTranslate.BTN_CANCEL }}</button>
                            <button type="submit" class="uta-btn uta-btn--main" :disabled="carGroupsDeleteProcess">{{ getTranslate.BTN_DELETE }}</button>
                        </div>
                    </form>
                </template>
            </Modal>
        </transition>
    </div>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/cars-groups.scss';
</style>